/**
 * Range checks for the maturity input dialogs (Nurse-Saul and Arrhenius).
 *
 * Two levels, matching backend services/maturity_service.py:
 *  - 'invalid': the server refuses the value (422), so the dialog blocks Save.
 *    Datum outside -10..10 °C, specified temperature outside 0..50 °C,
 *    activation energy not a whole number above 0 (or absurdly large).
 *  - 'unusual': allowed, but outside the typical range, so the dialog warns.
 *    Datum outside -5..5 °C, activation energy outside 30000..60000 J/mol
 *    (Q about 3600..7200 K), specified temperature other than 20 or 23 °C.
 *
 * Values are the dialogs' form strings. A blank or non-numeric value is
 * 'invalid' (the field is required).
 */

export type RangeLevel = 'ok' | 'unusual' | 'invalid';

export const DATUM_LIMITS = { min: -10, max: 10 } as const;
export const DATUM_TYPICAL = { min: -5, max: 5 } as const;
export const SPECIFIED_TEMP_LIMITS = { min: 0, max: 50 } as const;
export const SPECIFIED_TEMP_TYPICAL = [20, 23] as const;
export const ACTIVATION_ENERGY_MAX = 1_000_000;
export const ACTIVATION_ENERGY_TYPICAL = { min: 30000, max: 60000 } as const;
/** The typical activation energies as Q (AE / 8.31), rounded to 100 K. */
export const Q_TYPICAL = { min: 3600, max: 7200 } as const;

/** The form string as a finite number, or null when blank or not a number. */
export const parseNumber = (value: string | null | undefined): number | null => {
  const text = (value ?? '').trim();
  if (text === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
};

export const datumTempLevel = (value: string): RangeLevel => {
  const n = parseNumber(value);
  if (n === null || n < DATUM_LIMITS.min || n > DATUM_LIMITS.max) return 'invalid';
  return n < DATUM_TYPICAL.min || n > DATUM_TYPICAL.max ? 'unusual' : 'ok';
};

export const specifiedTempLevel = (value: string): RangeLevel => {
  const n = parseNumber(value);
  if (n === null || n < SPECIFIED_TEMP_LIMITS.min || n > SPECIFIED_TEMP_LIMITS.max) return 'invalid';
  return (SPECIFIED_TEMP_TYPICAL as readonly number[]).includes(n) ? 'ok' : 'unusual';
};

export const activationEnergyLevel = (value: string): RangeLevel => {
  const n = parseNumber(value);
  if (n === null || n <= 0 || !Number.isInteger(n) || n > ACTIVATION_ENERGY_MAX) return 'invalid';
  return n < ACTIVATION_ENERGY_TYPICAL.min || n > ACTIVATION_ENERGY_TYPICAL.max ? 'unusual' : 'ok';
};
